import React, { useRef, useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { ReactComponent as CloseModalIcon } from '../../../assets/svg/CloseModalIcon.svg';
import { ReactComponent as Save } from '../../../assets/svg/Save.svg';
import Loading from '../../../utils/Loading';
import { createFinanceCard } from '../../../server/server';
import { Controller, useForm } from 'react-hook-form';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

function AddNewFinanceCardModal({ modalIsOpen, closeModal, user = null, reloadList }: { modalIsOpen: any; closeModal: any; user: any; reloadList?: any }) {
    const [processing, setProcessing] = useState(false);
    const [valid, setValid] = useState(true);

    const { register, handleSubmit, watch, reset, formState, getValues, setValue, control } = useForm({
        defaultValues: {
            uplata: undefined,
            brojDana: undefined,
            datumUplate: undefined,
        },
    });
    const { isDirty, isValid, errors } = formState;

    const cleanUpFunction = () => {
        reset();
        setValid(true);
    };

    const onSubmit = async () => {
        setProcessing(true);
        const data = {
            userId: user?.id,
            datumUplate: getValues('datumUplate'),
            uplata: Number(getValues('uplata')),
            brojDana: Number(getValues('brojDana')),
        };
        const resp = await createFinanceCard(data);
        if (resp.status === 201) {
            reloadList();
            closeModal();
            setProcessing(false);
        } else {
            setProcessing(false);
        }
    };

    const changeValid = () => {
        setValid((prev) => !prev);
    };

    return (
        <ReactModal
            isOpen={modalIsOpen}
            onAfterOpen={() => {}}
            onAfterClose={() => cleanUpFunction()}
            onRequestClose={closeModal}
            style={{
                overlay: {
                    zIndex: 7,
                    background: '#00000045',
                },
                content: {
                    padding: 0,
                    paddingTop: 30,
                    paddingBottom: 30,
                    opacity: 1,
                    width: 800,
                    margin: 'auto',
                    backgroundColor: 'transparent',
                    maxHeight: '100vh',
                    border: '0px',
                    boxShadow: 'none',
                    overflow: 'auto',
                },
            }}
            className={'no-scroll outline-0'}
        >
            <div className="student-info-modal  ">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="close-modal-icon " onClick={() => closeModal()}>
                        <CloseModalIcon />
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px]">
                    <div className="flex-1">
                        <p className="text-blueLight font-semibold text-3xl text-left">Kreiranje nove finansijske kartice</p>
                    </div>
                    <div className="mt-5 ">
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr-cyrl">
                            <Controller
                                control={control}
                                name="datumUplate"
                                rules={{ required: 'Datum uplate je obavezan.' }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => {
                                    return (
                                        <DatePicker
                                            label="Datum uplate"
                                            value={value ? dayjs(value) : null}
                                            onChange={(date: any) => {
                                                onChange(date ? date.toDate() : null);
                                            }}
                                            disabled={false}
                                            slotProps={{
                                                textField: {
                                                    error: !!error, // Postavlja grešku
                                                    helperText: error ? error.message : null, // Prikazuje poruku greške
                                                },
                                            }}
                                        />
                                    );
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className="mt-5">
                        <p className="text-primaryViolet font-semibold text-lg text-left">Uplata</p>
                        <div className={`border-b p-2 mt-2 flex flex-row items-center ${errors.uplata ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
                            <input
                                className={`w-full text-primaryViolet ${errors.uplata ? 'placeholder:text-redError' : ''}`}
                                placeholder="Uplata"
                                {...register('uplata', {
                                    required: 'Polje je obavezno.', // Poruka za `required`
                                    validate: (value: any) => value > 0 || 'Uplata mora biti veća od nule.', // Poruka za broj ≤ 0
                                })}
                                style={{ backgroundColor: 'transparent' }}
                                type="number"
                            />
                            <p className="text-redError whitespace-nowrap">{errors.uplata?.message}</p>
                        </div>
                    </div>
                    <div className="mt-5">
                        <p className="text-primaryViolet font-semibold text-lg text-left">Broj dana</p>
                        <div className={`border-b p-2 mt-2 flex flex-row items-center ${errors.brojDana ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
                            <input
                                className={`w-full text-primaryViolet ${errors.brojDana ? 'placeholder:text-redError' : ''}`}
                                placeholder="Broj dana"
                                {...register('brojDana', {
                                    required: 'Broj dana je obavezno polje.', // Poruka za `required`
                                    validate: (value: any) => value > 0 || 'Broj dana mora biti veći od nule.', // Poruka za broj ≤ 0
                                })}
                                style={{ backgroundColor: 'transparent' }}
                                type="number"
                            />
                            <p className="text-redError whitespace-nowrap">{errors.brojDana?.message}</p>
                        </div>
                    </div>
                    <div className="w-full">
                        <div className={`flex flex-row items-center cursor-pointer my-2`}>
                            <div
                                className="border-[1.5px] border-black rounded-full h-[18px] w-[18px] flex items-center justify-center"
                                onClick={() => changeValid()}
                            >
                                {valid && <div className="rounded-full h-[10px] w-[10px] bg-black"></div>}
                            </div>
                            <label className="text-black text-[16px] font-medium ml-[13px] cursor-pointer not-selectable">Korisnik Validan</label>
                        </div>
                    </div>

                    <div className="flex flex-row justify-end w-full gap-5 mt-2">
                        <div
                            className="flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-[#fafaff] hover:bg-grey-300  h-[60px] button-transparent-class"
                            onClick={closeModal}
                        >
                            <p className="text-[18px] text-primaryViolet leading-5 font-semibold mr-3 ">Odustani</p>
                        </div>
                        <button
                            className="flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer  rounded-full bg-blueMedium  hover:bg-blueDark  min-h-[60px] button-save-class transition"
                            disabled={processing}
                        >
                            {processing ? (
                                <Loading primaryColor="#FDDF6D" secondaryColor="#5E639B" size={25} stroke={7} />
                            ) : (
                                <div className="flex flex-row items-center justify-center">
                                    <p className="text-[18px]  leading-5 font-semibold mr-3 text-white">Sačuvaj</p>
                                    <Save color="white" />
                                </div>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </ReactModal>
    );
}

export default AddNewFinanceCardModal;
